<script lang="ts">
  import { onMount } from "svelte"
  import { generateReferralLink } from "$lib/utils/referral"
  import { Button } from "$lib/components/ui/button"
  import { Input } from "$lib/components/ui/input"
  import { Skeleton } from "$lib/components/ui/skeleton"
  import * as Tooltip from "$lib/components/ui/tooltip"
  import { Copy } from "lucide-svelte"
  import { fade } from "svelte/transition"
  import {
    shareOnPlatform,
    platformIcons,
    platformColors,
    getRequirement,
  } from "$lib/components/Widget2/utils"
  import userStore from "$lib/stores/userStore"
  import { t } from "$lib/utils/i18n"

  let { action, competition, embed } = $props()

  let referralLink = $state("")
  let copyMessage = $state("")
  let copyMessageTimeout: ReturnType<typeof setTimeout> | undefined

  let sharingOptionsRequirement = $derived(
    action.requirements.find(
      (r) => r.name === "Enable sharing options (optional)",
    ),
  )
  let availableSharingOptions = $derived(
    sharingOptionsRequirement?.options || [],
  )
  let userSelectedSharingOptions = $derived(
    sharingOptionsRequirement?.value || [],
  )
  let sharingOptions = $derived(
    availableSharingOptions.filter((option) =>
      userSelectedSharingOptions.includes(option.value),
    ),
  )

  async function copyToClipboard() {
    if (embed) {
      window.parent.postMessage(
        { type: "COPY_TO_CLIPBOARD", data: referralLink },
        "*",
      )
    } else {
      await navigator.clipboard.writeText(referralLink)
    }
    copyMessage = "Copied!"

    if (copyMessageTimeout) clearTimeout(copyMessageTimeout)

    copyMessageTimeout = setTimeout(() => {
      copyMessage = ""
    }, 2000)
  }

  onMount(async () => {
    referralLink = await generateReferralLink({
      user: $userStore.user,
      competition: competition,
    })
  })
</script>

<div class="p-4 w-full sm:min-w-[436px]">
  <p class="text-sm mb-4">
    {t("widget_referral_extra_entries", {
      count: getRequirement(action, "Entries"),
      entries: t("widget_action_entries", {
        count: Number(getRequirement(action, "Entries")),
      }),
    })}
  </p>
  <div class="mb-4 w-full flex items-center gap-2">
    {#if referralLink}
      <div class="flex-1">
        <Input type="text" value={referralLink} readonly />
      </div>
    {:else}
      <div class="flex-1">
        <Skeleton class="h-9 w-full" />
      </div>
    {/if}
    <Button
      onclick={copyToClipboard}
      class="relative shrink-0"
      disabled={!referralLink}
    >
      {#if copyMessage}
        <span
          class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded"
          transition:fade
        >
          {copyMessage}
        </span>
      {/if}
      <Copy size={16} />
    </Button>
  </div>
  <div class="flex justify-center space-x-4 mt-4">
    {#each sharingOptions as option}
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger>
            <Button
              onclick={() =>
                shareOnPlatform(option.value, referralLink, competition.title)}
              class="{platformColors[option.value]} text-white p-2 h-10 w-10"
            >
              {#if platformIcons[option.value].svg}
                {@html platformIcons[option.value].svg}
              {:else}
                {@const SvelteComponent = platformIcons[option.value]}
                <SvelteComponent size={20} />
              {/if}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content side="left">
            <p>{t("widget_share_on_platform", { platform: option.label })}</p>
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    {/each}
  </div>
  <p class="text-xs mt-4 text-center text-gray-500">
    <!-- Note: To be credited for a referral, your friends must complete at least one
    action. -->
    {t("widget_referral_credit_note")}
  </p>
</div>
